// DNA Pay - Detalhe do Título
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  ArrowLeft, FileText, User, Calendar, DollarSign,
  CreditCard, QrCode, Barcode, CheckCircle, Clock,
  AlertTriangle, XCircle, Loader2, History, Bell,
  Copy, Send,
  Edit2, Ban, RotateCcw, Undo2, Settings, Receipt, Wallet,
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useTituloActions } from '@/hooks/useTituloActions';
import HistoricoRecebimentos from './components/HistoricoRecebimentos';
import PaymentSettlementModal from '@/components/financeiro/PaymentSettlementModal';

const API_URL = window.location.origin;

const statusConfig = {
  pendente: { label: 'Pendente', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  pago: { label: 'Pago', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  vencido: { label: 'Vencido', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
  cancelado: { label: 'Cancelado', color: 'bg-gray-100 text-gray-700', icon: XCircle },
  em_processamento: { label: 'Processando', color: 'bg-blue-100 text-blue-700', icon: Loader2 },
  estornado: { label: 'Estornado', color: 'bg-purple-100 text-purple-700', icon: XCircle },
};

export default function DnaPayTituloDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [titulo, setTitulo] = useState(null);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    loadTitulo();
    loadTimeline();
  }, [id]);

  // Hook centralizado de ações em título — recarrega titulo + timeline após cada ação
  const reloadAll = () => { loadTitulo(); loadTimeline(); };
  const { openAction: openTituloAction, isAdmin: isAdminUser, dialogNode: tituloActionDialog } = useTituloActions({ onChanged: reloadAll });

  const loadTitulo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dnapay/billing/titulos/${id}`);
      if (response.ok) {
        setTitulo(await response.json());
      } else {
        toast.error('Título não encontrado');
        navigate('/v2/financeiro/dnapay/titulos');
      }
    } catch (error) {
      toast.error('Erro ao carregar título');
    } finally {
      setLoading(false);
    }
  };

  const loadTimeline = async () => {
    try {
      const response = await fetch(`${API_URL}/api/dnapay/audit/timeline/titulo/${id}`);
      if (response.ok) {
        const data = await response.json();
        setTimeline(data.timeline || []);
      }
    } catch (error) {
      console.error('Erro ao carregar timeline:', error);
    }
  };

  const handleEnviarNotificacao = async (tipo) => {
    try {
      const response = await fetch(`${API_URL}/api/dnapay/notificacoes/enviar/${id}?tipo=${tipo}&canal=whatsapp`, {
        method: 'POST'
      });
      
      if (response.ok) {
        toast.success('Notificação enviada!');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erro ao enviar notificação');
      }
    } catch (error) {
      toast.error('Erro ao enviar notificação');
    }
  };

  const [settleOpen, setSettleOpen] = useState(false);

  const verRecibo = async () => {
    try {
      const tid = titulo?.title_id || id;
      const r = await api.get(`/financial/titulos/${tid}/recibo.jpg`, { responseType: 'blob' });
      window.open(URL.createObjectURL(r.data), '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível gerar o recibo.');
    }
  };

  const enviarRecibo = async () => {
    try {
      const tid = titulo?.title_id || id;
      const { data } = await api.get(`/financial/titulos/${tid}/recibo/share`);
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.image_path}`;
      const msg = encodeURIComponent(`${data.text}${url}`);
      window.open(data.phone ? `https://wa.me/${data.phone}?text=${msg}` : `https://wa.me/?text=${msg}`, '_blank');
      toast.success(data.phone
        ? `Recibo pronto para ${data.payer_name || 'o responsável'} — confirme o envio no WhatsApp.`
        : 'Responsável sem telefone cadastrado: escolha o contato no WhatsApp.');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível preparar o recibo.');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : String(dateStr);
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!titulo) return null;

  const StatusBadge = ({ status }) => {
    const config = statusConfig[status] || statusConfig.pendente;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} gap-1 text-sm px-3 py-1`}>
        <Icon className="w-4 h-4" />
        {config.label}
      </Badge>
    );
  };

  return (
    <div className="space-y-6 p-6" data-testid="dnapay-titulo-detalhe">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/v2/financeiro/dnapay/titulos')}>
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{titulo.descricao}</h1>
          <p className="text-gray-500">Título #{titulo.id.slice(0, 8)}</p>
        </div>
        <StatusBadge status={titulo.status} />
        {(!['pago', 'cancelado', 'renegociado'].includes(titulo.status)
          || Number(titulo.saldo_atualizado ?? titulo.balance ?? 0) > 0.009) && titulo.status !== 'cancelado' && (
          <Button size="sm" className="bg-violet-700 hover:bg-violet-800" data-testid="btn-registrar-recebimento"
            onClick={() => setSettleOpen(true)}>
            <Wallet className="w-4 h-4 mr-1.5" /> Registrar recebimento
          </Button>
        )}
        {['pago', 'parcial'].includes(titulo.status) && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={verRecibo} data-testid="btn-recibo-jpeg">
              <Receipt className="w-4 h-4 mr-1.5" /> Recibo em JPEG
            </Button>
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={enviarRecibo}
              data-testid="btn-recibo-whatsapp">
              <Send className="w-4 h-4 mr-1.5" /> Enviar recibo no WhatsApp
            </Button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna Principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Informações do Título */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-purple-600" />
                Informações da Cobrança
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">Categoria</p>
                  <p className="font-medium capitalize">{titulo.categoria}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Competência</p>
                  <p className="font-medium">{titulo.competencia || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Vencimento</p>
                  <p className="font-medium">{formatDate(titulo.vencimento)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Data Pagamento</p>
                  <p className="font-medium">{formatDate(titulo.data_pagamento)}</p>
                </div>
              </div>
              
              <Separator />
              
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Valor Original</p>
                  <p className="font-bold">{formatCurrency(titulo.valor_original)}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-gray-500">Desconto</p>
                  <p className="font-bold text-green-600">- {formatCurrency(titulo.desconto)}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs text-gray-500">Encargos</p>
                  <p className="font-bold text-red-600">+ {formatCurrency((titulo.multa || 0) + (titulo.juros || 0))}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-gray-500">Saldo Atualizado</p>
                  <p className="font-bold text-purple-600">{formatCurrency(titulo.saldo_atualizado)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Dados de Pagamento */}
          {(titulo.pix_qr_code || titulo.linha_digitavel || titulo.link_cartao) && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  Dados de Pagamento
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {titulo.pix_qr_code && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <QrCode className="w-5 h-5 text-green-600" />
                      <span className="font-medium">PIX</span>
                    </div>
                    <div className="flex justify-center mb-4">
                      <img src={titulo.pix_qr_code} alt="QR Code PIX" className="w-32 h-32" />
                    </div>
                    <div className="flex gap-2">
                      <code className="flex-1 p-2 bg-white rounded text-xs truncate">{titulo.pix_copia_cola}</code>
                      <Button variant="outline" size="icon" onClick={() => {
                        navigator.clipboard.writeText(titulo.pix_copia_cola);
                        toast.success('Código PIX copiado!');
                      }}>
                        <Copy className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
                
                {titulo.linha_digitavel && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Barcode className="w-5 h-5 text-blue-600" />
                      <span className="font-medium">Boleto</span>
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-gray-500">Nosso Número</p>
                        <p className="font-mono">{titulo.nosso_numero}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Linha Digitável</p>
                        <div className="flex gap-2">
                          <code className="flex-1 p-2 bg-white rounded text-xs">{titulo.linha_digitavel}</code>
                          <Button variant="outline" size="icon" onClick={() => {
                            navigator.clipboard.writeText(titulo.linha_digitavel);
                            toast.success('Linha digitável copiada!');
                          }}>
                            <Copy className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <HistoricoRecebimentos titleId={titulo.title_id || id} onChanged={reloadAll}
            canReverse={isAdminUser} />

          {/* Timeline */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-purple-600" />
                Histórico
              </CardTitle>
            </CardHeader>
            <CardContent>
              {timeline.length === 0 ? (
                <p className="text-center text-gray-500 py-4">Nenhum evento registrado</p>
              ) : (
                <div className="space-y-4">
                  {timeline.map((evento, idx) => (
                    <div key={idx} className="flex gap-3">
                      <div className="w-2 h-2 mt-2 rounded-full bg-purple-600" />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{evento.descricao}</p>
                        <p className="text-xs text-gray-500">{formatDateTime(evento.created_at)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Coluna Lateral */}
        <div className="space-y-6">
          {/* Aluno */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5 text-purple-600" />
                Aluno
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Nome</p>
                <p className="font-medium">{titulo.aluno_nome}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Turma</p>
                <p className="font-medium">{titulo.turma_nome || '-'}</p>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-gray-500">Responsável Financeiro</p>
                <p className="font-medium">{titulo.responsavel_nome || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">WhatsApp</p>
                <p className="font-medium">{titulo.responsavel_whatsapp || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="font-medium">{titulo.responsavel_email || '-'}</p>
              </div>
            </CardContent>
          </Card>

          {/* Gerenciar título — Editar / Cancelar / Restaurar / Estornar / Reverter */}
          <Card data-testid="manage-titulo-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5 text-purple-600" />
                Gerenciar título
              </CardTitle>
              <CardDescription className="text-xs">
                Toda alteração registra log auditado
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {(() => {
                const st = String(titulo.status || '').toLowerCase();
                const isPaid = st === 'pago';
                const isCancelled = st === 'cancelado';
                const editBlocked = isCancelled || (isPaid && !isAdminUser);
                const editTitle = isCancelled
                  ? 'Título cancelado não pode ser editado. Restaure-o antes.'
                  : (isPaid && !isAdminUser)
                    ? 'Título pago só pode ser editado por administrador. Estorne o pagamento primeiro.'
                    : 'Editar título';
                const hasEstornoSnapshot = !!titulo.estorno_payment_snapshot;
                return (
                  <>
                    <Button
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => openTituloAction('edit', titulo)}
                      disabled={editBlocked}
                      title={editTitle}
                      data-testid={`btn-edit-${titulo.id}`}
                    >
                      <Edit2 className="w-4 h-4 mr-2 text-blue-600" />
                      Editar
                    </Button>
                    {!isCancelled && !isPaid && (
                      <Button
                        variant="outline"
                        className="w-full justify-start text-rose-700 border-rose-200 hover:bg-rose-50"
                        onClick={() => openTituloAction('cancel', titulo)}
                        data-testid={`btn-cancel-${titulo.id}`}
                      >
                        <Ban className="w-4 h-4 mr-2" />
                        Cancelar (com motivo)
                      </Button>
                    )}
                    {isCancelled && (
                      <Button
                        variant="outline"
                        className="w-full justify-start text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => openTituloAction('restore', titulo)}
                        data-testid={`btn-restore-${titulo.id}`}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Restaurar
                      </Button>
                    )}
                    {isPaid && (
                      <Button
                        variant="outline"
                        className="w-full justify-start text-amber-700 border-amber-200 hover:bg-amber-50"
                        onClick={() => openTituloAction('estornar', titulo)}
                        data-testid={`btn-estornar-${titulo.id}`}
                      >
                        <Undo2 className="w-4 h-4 mr-2" />
                        Estornar pagamento
                      </Button>
                    )}
                    {hasEstornoSnapshot && !isPaid && (
                      <Button
                        variant="outline"
                        className="w-full justify-start text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                        onClick={() => openTituloAction('reverter', titulo)}
                        data-testid={`btn-reverter-${titulo.id}`}
                      >
                        <RotateCcw className="w-4 h-4 mr-2" />
                        Reverter estorno
                      </Button>
                    )}
                  </>
                );
              })()}
            </CardContent>
          </Card>

          {/* Ações */}
          {titulo.status !== 'pago' && titulo.status !== 'cancelado' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5 text-purple-600" />
                  Ações
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => handleEnviarNotificacao('vencimento_proximo')}
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar Lembrete
                </Button>
                {titulo.status === 'vencido' && (
                  <Button 
                    variant="outline" 
                    className="w-full justify-start text-red-600"
                    onClick={() => handleEnviarNotificacao('titulo_vencido')}
                  >
                    <AlertTriangle className="w-4 h-4 mr-2" />
                    Cobrar Inadimplência
                  </Button>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Hook centralizado de ações em título (Editar / Cancelar / Restaurar / Estornar / Reverter) */}
      {tituloActionDialog}
      <PaymentSettlementModal open={settleOpen} onOpenChange={setSettleOpen}
        titleId={titulo?.title_id || id} title={titulo?.description}
        onSettled={reloadAll} />
    </div>
  );
}
